import { useMemo, useState, useCallback } from "react";
import { useGetList, Loading } from "react-admin";
import { useController, useFormContext } from "react-hook-form";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  Box,
  Chip,
  FormHelperText,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const RESOURCE_GROUP_LABELS: Record<string, string> = {
  file: "📁 Arquivos",
  course: "📚 Cursos",
  user: "👤 Usuários",
  group: "👥 Grupos",
  "job-application": "📋 Candidaturas",
  "job-application-source": "📍 Fontes de Candidatura",
  "job-application-status": "🏷️ Status de Candidatura",
  ticket: "🎫 Tickets",
  message: "💬 Mensagens",
  company: "🏢 Empresas",
  role: "🔐 Perfis",
  permission: "🔑 Permissões",
  report: "📊 Relatórios",
  other: "Outros",
};

const RESOURCE_GROUP_ORDER = [
  "user",
  "group",
  "role",
  "permission",
  "company",
  "course",
  "file",
  "job-application",
  "job-application-source",
  "job-application-status",
  "ticket",
  "message",
  "report",
];

interface Permission {
  id: number;
  code: string;
  description: string;
  resourceType: string;
}

interface GroupedPermissionsInputProps {
  source: string;
}

export const GroupedPermissionsInput = ({ source }: GroupedPermissionsInputProps) => {
  const { control } = useFormContext();
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

  // source="permissions" for Edit (array of objects), source="permissionIds" for Create (array of IDs)
  const { field: { value: sourceValue } } = useController({ name: source, control });
  
  // Always write to permissionIds for save
  const { field: { value: permissionIdsValue, onChange } } = useController({ name: "permissionIds", control });

  const { data: allPermissions, isLoading } = useGetList<Permission>("permission", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "resourceType", order: "ASC" },
  });

  // Derive selected IDs: prioritize permissionIds, then source value
  const selectedIds = useMemo((): number[] => {
    if (Array.isArray(permissionIdsValue) && permissionIdsValue.length > 0) {
      return permissionIdsValue;
    }
    if (Array.isArray(sourceValue) && sourceValue.length > 0) {
      // Handle both [{id: 1}, ...] and [1, 2, 3] formats
      return typeof sourceValue[0] === "object" 
        ? sourceValue.map((p: { id: number }) => p.id)
        : sourceValue;
    }
    return [];
  }, [sourceValue, permissionIdsValue]);

  // Group permissions by resourceType
  const { groupedPermissions, sortedGroups } = useMemo(() => {
    if (!allPermissions) return { groupedPermissions: {}, sortedGroups: [] };

    const grouped = allPermissions.reduce<Record<string, Permission[]>>((acc, p) => {
      const group = p.resourceType || "other";
      (acc[group] ??= []).push(p);
      return acc;
    }, {});

    const sorted = Object.keys(grouped).sort((a, b) => {
      const indexA = RESOURCE_GROUP_ORDER.indexOf(a);
      const indexB = RESOURCE_GROUP_ORDER.indexOf(b);
      if (indexA === -1 && indexB === -1) return a.localeCompare(b);
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    });

    return { groupedPermissions: grouped, sortedGroups: sorted };
  }, [allPermissions]);

  const togglePermission = useCallback((id: number) => {
    const newValue = selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    onChange(newValue);
  }, [selectedIds, onChange]);

  const toggleGroup = useCallback((group: string) => {
    const groupIds = groupedPermissions[group].map((p) => p.id);
    const allSelected = groupIds.every((id) => selectedIds.includes(id));
    
    const newValue = allSelected
      ? selectedIds.filter((id) => !groupIds.includes(id))
      : [...new Set([...selectedIds, ...groupIds])];
    onChange(newValue);
  }, [groupedPermissions, selectedIds, onChange]);

  const toggleAccordion = useCallback((group: string) => {
    setExpandedGroups((prev) =>
      prev.includes(group) ? prev.filter((g) => g !== group) : [...prev, group]
    );
  }, []);

  if (isLoading) return <Loading />;

  return (
    <Box sx={{ width: "100%", mb: 2 }}>
      <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
        Permissões
      </Typography>

      {sortedGroups.map((group) => {
        const permissions = groupedPermissions[group];
        const selectedCount = permissions.filter((p) => selectedIds.includes(p.id)).length;
        const allSelected = selectedCount === permissions.length;
        const someSelected = selectedCount > 0 && !allSelected;

        return (
          <Accordion
            key={group}
            expanded={expandedGroups.includes(group)}
            onChange={() => toggleAccordion(group)}
            sx={{ mb: 0.5 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1 } }}
            >
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onClick={(e) => { e.stopPropagation(); toggleGroup(group); }}
                size="small"
              />
              <Typography sx={{ flexGrow: 1 }}>
                {RESOURCE_GROUP_LABELS[group] || group}
              </Typography>
              <Chip
                label={`${selectedCount}/${permissions.length}`}
                size="small"
                color={allSelected ? "primary" : "default"}
                variant={selectedCount > 0 ? "filled" : "outlined"}
              />
            </AccordionSummary>
            <AccordionDetails>
              <FormGroup sx={{ pl: 4 }}>
                {permissions.map((permission) => (
                  <FormControlLabel
                    key={permission.id}
                    control={
                      <Checkbox
                        checked={selectedIds.includes(permission.id)}
                        onChange={() => togglePermission(permission.id)}
                        size="small"
                      />
                    }
                    label={permission.description || permission.code}
                  />
                ))}
              </FormGroup>
            </AccordionDetails>
          </Accordion>
        );
      })}

      <FormHelperText>Selecione as permissões para este perfil</FormHelperText>
    </Box>
  );
};
